import React, { useEffect, useRef, useState } from 'react';
import type { ComponentProps, HTMLAttributes } from 'react';
import styled from '@emotion/styled';
import type { Nullable } from 'corgi-types';
import { flexDirection, width } from 'styled-system';

import type { HTMLAutoCompleteAttribute } from '../../../enums/form';
import { FieldChangeTrigger } from '../../../enums/form';
import { InputType } from '../../../enums/input-type';
import type { FieldChangeTriggerHandler } from '../../../types/form';
import { getHTMLElementName } from '../../../utils/form/name';
import Input from '../../Input';
import StatefulInput from '../../Input/StatefulInput';
import { FieldInfo } from '../FieldInfo';
import { getTextFieldChangeHandler, getTrimmedValue } from './utils';
import type { TextFieldReplace } from './utils';

export interface TextFieldProps<FieldName extends string = string> {
  label: string;
  type?: InputType;
  name: FieldName;
  value?: Nullable<string>;
  onChange: FieldChangeTriggerHandler<string>;
  /**
   * @todo
   * once we get rid of an old src/common/components/Input/index.ts
   * remove `isStateful` prop please
   */
  isStateful?: boolean;
  enableAutoFocus?: boolean;
  autoComplete?: HTMLAutoCompleteAttribute;
  hasError?: boolean;
  hasWarning?: boolean;
  hasSuccess?: boolean;
  errors?: string[];
  warnings?: string[];
  successes?: string[];
  info?: string;
  regex?: RegExp;
  maxLength?: number;
  required?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  replace?: TextFieldReplace;
  trimValue?: boolean;
  inputMode?: HTMLAttributes<HTMLInputElement>['inputMode'];
  nameSuffix?: string;
  placeholder?: string;
}

interface StateProps {
  hasError?: boolean;
  hasWarning?: boolean;
  hasSuccess?: boolean;
  errors?: string[];
  warnings?: string[];
  successes?: string[];
}

const UnstyledWrapper = styled.label<any>`
  ${width};
  ${flexDirection};

  > * {
    ${width};
  }
`;

const Wrapper = (props: ComponentProps<typeof UnstyledWrapper>) => (
  <UnstyledWrapper width={1} flexDirection="column" {...props} />
);

const UnstyledStyledInput = styled(Input)``;

const StyledInput = (props: ComponentProps<typeof UnstyledStyledInput>) => (
  <UnstyledStyledInput mb="sp_0" {...props} />
);

const UnstyledStyledStatefulInput = styled(StatefulInput)``;

const StyledStatefulInput = (props: ComponentProps<typeof UnstyledStyledStatefulInput>) => (
  <UnstyledStyledStatefulInput mb="sp_0" {...props} />
);

const getStateProps = ({ errors, warnings, successes, ...restProps }: StateProps) => {
  const { hasError, hasWarning, hasSuccess } = restProps;

  return {
    ...restProps,
    ...(hasError && { errors }),
    ...(hasWarning && { warnings }),
    ...(hasSuccess && { successes }),
  };
};

const TextField = ({
  label,
  name,
  type = InputType.Text,
  isStateful = true,
  enableAutoFocus,
  autoComplete,
  hasWarning,
  hasSuccess,
  warnings,
  successes,
  info,
  disabled,
  readOnly,
  hasError,
  errors,
  value,
  onChange,
  replace,
  regex,
  maxLength,
  required = false,
  trimValue = false,
  inputMode,
  nameSuffix,
  placeholder,
}: TextFieldProps) => {
  const innerRef = useRef<Nullable<HTMLInputElement>>(null);
  const [inputValue, setInputValue] = useState(value || '');
  const InputComponent = isStateful ? StyledStatefulInput : StyledInput;
  const stateProps = getStateProps({
    hasError,
    hasWarning,
    hasSuccess,
    errors,
    warnings,
    successes,
  });

  const handleChange = getTextFieldChangeHandler({
    name,
    regex,
    maxLength,
    hasError,
    replace,
    setInputValue,
    onChange,
  });

  const handleBlur = () =>
    onChange({
      fieldName: name,
      value: getTrimmedValue(trimValue, inputValue),
      trigger: FieldChangeTrigger.Blur,
    });

  useEffect(() => {
    if (enableAutoFocus && innerRef.current) {
      innerRef.current.focus();

      onChange({
        fieldName: name,
        value: innerRef.current.value,
        trigger: FieldChangeTrigger.Change,
      });
    }
  }, []);

  useEffect(() => {
    if (value) {
      setInputValue(value);
    }
  }, [value]);

  return (
    <Wrapper>
      <InputComponent
        type={type}
        ref={innerRef}
        value={inputValue}
        name={getHTMLElementName(name, nameSuffix)}
        aria-label={label}
        aria-invalid={hasError}
        placeholder={placeholder ?? label}
        label={label}
        aria-required={required}
        disabled={disabled}
        readOnly={readOnly}
        onChange={handleChange}
        onBlur={handleBlur}
        {...(autoComplete ? { autoComplete } : {})}
        {...(inputMode ? { inputMode } : {})}
        {...stateProps}
      />
      <FieldInfo info={info} />
    </Wrapper>
  );
};

export default TextField;
